/*
 * Copyright (c) 2025 Bolmate B.V.
 */

// Keep boundary values in sync with breakpoint-tiers.scss (SCSS).
export const BP = {
    TINY: 360,
    MOBILE: 600,
    TABLET_SMALL_END: 768,
    DESKTOP: 1024,
    DESKTOP_FULL: 1280,
    LARGE: 1920
} as const;

export interface BreakpointState {
    isTiny: boolean;
    isMobile: boolean;
    isTabletSmall: boolean;
    isTablet: boolean;
    isDesktopSmall: boolean;
    isDesktop: boolean;
    isLarge: boolean;
    /** The bottom-nav layout range (< DESKTOP_FULL): sidebar hidden, bottom
     *  navigation shown. Single source of truth — don't re-derive it from the
     *  individual flags at call sites. */
    isMobileLayout: boolean;
    width: number;
}

type Listener = () => void;

const QUERIES: Record<keyof Omit<BreakpointState, 'width'>, string> = {
    isTiny: `(max-width: ${BP.TINY - 1}px)`,
    isMobile: `(max-width: ${BP.MOBILE - 1}px)`,
    isTabletSmall: `(min-width: ${BP.MOBILE}px) and (max-width: ${BP.TABLET_SMALL_END - 1}px)`,
    isTablet: `(min-width: ${BP.MOBILE}px) and (max-width: ${BP.DESKTOP - 1}px)`,
    isDesktopSmall: `(min-width: ${BP.DESKTOP}px) and (max-width: ${BP.DESKTOP_FULL - 1}px)`,
    isDesktop: `(min-width: ${BP.DESKTOP}px)`,
    isLarge: `(min-width: ${BP.LARGE}px)`,
    isMobileLayout: `(max-width: ${BP.DESKTOP_FULL - 1}px)`
};

/** Hardware "this is a phone/tablet" check — same rule as pwaUtil's
 *  isTouchFirstDevice(), inlined here to keep this module import-free: a real
 *  touchscreen plus a primary pointer that is coarse / cannot hover. A laptop
 *  with a touchscreen keeps its trackpad, so it stays 'fine' + 'hover'. */
function isTouchFirstDevice(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
    const touchPoints = navigator.maxTouchPoints ?? 0;
    if (touchPoints < 1) return false;
    const platform = (navigator as unknown as {platform?: string}).platform ?? '';
    if (/Mac/i.test(platform) || /Macintosh/.test(navigator.userAgent)) return touchPoints > 1;
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(pointer: coarse)').matches || window.matchMedia('(hover: none)').matches;
}

function computeState(): BreakpointState {
    // "Desktop site" on a phone rewrites the layout viewport to ~980px, which
    // flipped the whole app into the desktop layout on a 6-inch screen (and with
    // it the desktop-only views, the sidebar instead of the bottom bar, and the
    // panning buffer). The device is still a phone, so fall back to the physical
    // screen width. The touch check is inlined rather than imported from pwaUtil:
    // this module is pulled in by nearly every view and must stay dependency-free
    // (pwaUtil drags in workbox + i18n at module scope).
    const w = isTouchFirstDevice() ? Math.min(window.innerWidth, window.screen.width) : window.innerWidth;
    return {
        isTiny: w < BP.TINY,
        isMobile: w < BP.MOBILE,
        isTabletSmall: w >= BP.MOBILE && w < BP.TABLET_SMALL_END,
        isTablet: w >= BP.MOBILE && w < BP.DESKTOP,
        isDesktopSmall: w >= BP.DESKTOP && w < BP.DESKTOP_FULL,
        isDesktop: w >= BP.DESKTOP,
        isLarge: w >= BP.LARGE,
        isMobileLayout: w < BP.DESKTOP_FULL,
        width: w
    };
}

class BreakpointService {
    private state: BreakpointState;
    private listeners = new Set<Listener>();

    constructor() {
        this.state = computeState();
        this.setupMediaListeners();
    }

    private setupMediaListeners() {
        const handler = () => {
            const next = computeState();
            if (this.flagsChanged(next)) {
                this.state = next;
                for (const fn of this.listeners) fn();
            }
        };
        for (const query of Object.values(QUERIES)) {
            window.matchMedia(query).addEventListener('change', handler);
        }
    }

    private flagsChanged(next: BreakpointState): boolean {
        return (
            this.state.isTiny !== next.isTiny ||
            this.state.isMobile !== next.isMobile ||
            this.state.isTabletSmall !== next.isTabletSmall ||
            this.state.isTablet !== next.isTablet ||
            this.state.isDesktopSmall !== next.isDesktopSmall ||
            this.state.isDesktop !== next.isDesktop ||
            this.state.isLarge !== next.isLarge ||
            this.state.isMobileLayout !== next.isMobileLayout
        );
    }

    getSnapshot = (): BreakpointState => this.state;

    subscribe = (listener: Listener): (() => void) => {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    };
}

export const breakpointService = new BreakpointService();
